/**
 * Active Reservations Hook
 * Custom hook for managing active reservation data
 */

import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../../../contexts/AuthContext';
import axios from 'axios';

export const useActiveReservations = (options = {}) => {
  const { 
    autoRefresh = true, 
    refreshInterval = 2 * 60 * 1000, // 2 minutes
    maxReservations = 10,
    includeCompleted = false
  } = options;

  const { token, loading: authLoading } = useAuth();
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);

  const fetchActiveReservations = useCallback(async () => {
    if (!token || authLoading) return;

    try {
      setLoading(true);
      setError(null);

      // Ensure authorization header is set
      if (token && !axios.defaults.headers.common['Authorization']) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      }

      const params = new URLSearchParams();
      if (!includeCompleted) {
        params.append('status', 'ACTIVE');
      }
      if (maxReservations) {
        params.append('limit', maxReservations.toString());
      }

      const response = await axios.get(`/api/reservations/integration/all?${params.toString()}`);
      
      // Handle different response formats
      const reservationData = Array.isArray(response.data) 
        ? response.data 
        : Array.isArray(response.data.reservations) 
          ? response.data.reservations 
          : [];

      setReservations(reservationData);
      setLastUpdated(new Date());
    } catch (err) {
      console.error('Error fetching active reservations:', err);
      setError(`Failed to load active reservations: ${err.response?.data?.message || err.message}`);
      setReservations([]);
    } finally {
      setLoading(false);
    }
  }, [token, authLoading, maxReservations, includeCompleted]);

  const refresh = useCallback(() => {
    return fetchActiveReservations();
  }, [fetchActiveReservations]);

  // Initial fetch
  useEffect(() => {
    if (token && !authLoading) {
      fetchActiveReservations();
    }
  }, [token, authLoading, fetchActiveReservations]);

  // Auto-refresh interval
  useEffect(() => {
    if (!autoRefresh || !token || authLoading) return;

    const interval = setInterval(() => {
      fetchActiveReservations();
    }, refreshInterval);

    return () => clearInterval(interval);
  }, [autoRefresh, refreshInterval, token, authLoading, fetchActiveReservations]);

  // Utility functions for reservation processing
  const getReservationsByStatus = useCallback((status) => {
    return reservations.filter(reservation => reservation.status === status);
  }, [reservations]);

  const getOverdueReservations = useCallback(() => {
    const now = new Date();
    return reservations.filter(reservation => {
      if (!reservation.endTime) return false;
      const endTime = new Date(reservation.endTime);
      return endTime < now && reservation.status === 'ACTIVE';
    });
  }, [reservations]);

  const getTotalActiveTime = useCallback(() => {
    const now = new Date();
    return reservations.reduce((total, reservation) => {
      if (reservation.status !== 'ACTIVE' || !reservation.startTime) return total;
      const startTime = new Date(reservation.startTime);
      const activeTime = now - startTime;
      return total + Math.max(0, activeTime);
    }, 0);
  }, [reservations]);

  return {
    reservations,
    loading,
    error,
    lastUpdated,
    refresh,
    refetch: fetchActiveReservations,
    // Utility functions
    getReservationsByStatus,
    getOverdueReservations,
    getTotalActiveTime,
    // Computed values
    activeCount: reservations.filter(r => r.status === 'ACTIVE').length,
    overdueCount: getOverdueReservations().length
  };
};